import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { StatusChip } from '../ds/components/StatusChip';
import { statusToDS } from '../ds/StatusMap';
import { buildingsApi, UncheckedApartment, UncheckedApartmentsResponse } from '../services/api';
import {
  attemptsText,
  groupUnchecked,
  totalUnchecked,
  UNCHECKED_GROUP_LABELS,
  UNCHECKED_GROUP_ORDER,
  uncheckedCsv,
  uncheckedStatusLabel,
  uncheckedText,
} from '../utils/uncheckedApartments';
import { asciiFilename, saveBlob } from '../utils/download';
import { apartmentDoorMarking } from '../utils/apartmentLabels';
import { budynkiLabel, lokaleLabel } from '../utils/plural';

export interface UncheckedApartmentsModalProps {
  /** Budynki do zestawienia; `null` = modal zamknięty. */
  buildingIds: string[] | null;
  /** Dopisek do tytułu i nazwy pliku CSV (np. adres jednego budynku). */
  subject?: string;
  onClose: () => void;
}

const banner = (kind: 'error' | 'ok'): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-label)',
  background: kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-ok-bg)',
  border: `var(--border-width) solid ${kind === 'error' ? 'var(--status-deny-bar)' : 'var(--status-ok-bar)'}`,
  color: kind === 'error' ? 'var(--status-deny)' : 'var(--status-ok)',
});

const groupTitle: React.CSSProperties = {
  margin: '0 0 var(--space-1)',
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)' as any,
  color: 'var(--text-heading)',
  textTransform: 'none',
};

const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: 'var(--border-width) solid var(--border)',
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};

/**
 * „Niesprawdzone lokale" (właściciel 2026-10-02, docx): lokale BEZ terminalnego wyniku w bieżącym
 * przeglądzie — jak przycisk „Tylko niezamknięte" na tablecie — dla jednego budynku (szczegóły)
 * albo wielu (pasek zaznaczenia w Budynkach). Grupy: Nieobecność / Odmowa / Niesprawdzone (E13/X10).
 * Udostępnienie: tekst do schowka
 * (do wklejenia w mail dla zarządcy) albo CSV (Excel, separator `;`).
 */
export default function UncheckedApartmentsModal({ buildingIds, subject, onClose }: UncheckedApartmentsModalProps) {
  const [data, setData] = useState<UncheckedApartmentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copyMsg, setCopyMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  // Klucz zamiast tablicy: rodzic podaje nowy literał co render, a pobieramy tylko przy zmianie wyboru.
  const key = buildingIds && buildingIds.length ? JSON.stringify(buildingIds) : '';
  useEffect(() => {
    setCopyMsg(null);
    if (!key) { setData(null); setError(null); return undefined; }
    let stale = false; // guard anty-stale: zamknięcie/zmiana wyboru w trakcie nie nadpisze wyniku
    setLoading(true);
    setData(null);
    setError(null);
    buildingsApi.uncheckedApartments(JSON.parse(key))
      .then((res) => { if (!stale) setData(res); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : 'Nie udało się pobrać niesprawdzonych lokali'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [key]);

  const total = data ? totalUnchecked(data) : 0;

  const exportCsv = () => {
    if (!data) return;
    const blob = new Blob([uncheckedCsv(data)], { type: 'text/csv;charset=utf-8' });
    const day = new Date().toISOString().slice(0, 10);
    saveBlob(blob, `niesprawdzone_lokale_${asciiFilename(subject || 'budynki')}_${day}.csv`);
  };

  const copyText = async () => {
    if (!data) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(uncheckedText(data));
      setCopyMsg({ kind: 'ok', text: 'Skopiowano listę — wklej ją np. w mail do zarządcy.' });
    } catch {
      // Schowek bywa niedostępny (http bez TLS, brak zgody przeglądarki) — CSV działa zawsze.
      setCopyMsg({ kind: 'error', text: 'Przeglądarka nie pozwoliła skopiować — użyj „Pobierz CSV".' });
    }
  };

  const aptRows = (list: UncheckedApartment[]) => list.map((a) => (
    <tr key={a.id}>
      <td style={{ ...td, fontWeight: 'var(--weight-semibold)' as any, whiteSpace: 'nowrap' }}>
        Lokal {a.apartmentNumber}
        {/* E3: oznaczenie na drzwiach z panelu pod numerem CEEB (jak w tabeli lokali budynku). */}
        {apartmentDoorMarking(a) && (
          <div
            data-testid={`unchecked-marking-${a.id}`}
            style={{ marginTop: '2px', fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-regular)' as any, color: 'var(--text-secondary)' }}
          >
            {apartmentDoorMarking(a)}
          </div>
        )}
      </td>
      <td style={td}>
        <StatusChip status={a.status ? statusToDS(a.status) : 'todo'} label={uncheckedStatusLabel(a.status)} size="sm" />
      </td>
      <td style={{ ...td, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
        {a.attemptDates.length ? `Próby: ${attemptsText(a)}` : ''}
      </td>
    </tr>
  ));

  return (
    <ModalShell
      open={!!key}
      title={subject ? `Niesprawdzone lokale — ${subject}` : 'Niesprawdzone lokale'}
      width={760}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Zamknij</Button>
          <Button variant="secondary" icon="clipboard" onClick={copyText} disabled={!data || total === 0}>
            Kopiuj listę
          </Button>
          <Button variant="primary" icon="download" onClick={exportCsv} disabled={!data || total === 0}>
            Pobierz CSV (Excel)
          </Button>
        </>
      }
    >
      <div data-testid="unchecked-apartments" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Lokale bez wyniku zamykającego w bieżącym przeglądzie, w trzech grupach: nieobecność i odmowa
          lokatora (lokale do ponownej wizyty) oraz lokale jeszcze niesprawdzone. Odmowa wpisu do rejestru
          CEEB zamyka lokal, więc nie ma jej na tej liście (w „Nieudostępnione" w PDF i na tablecie jest liczona).
        </p>
        {loading && <div style={{ color: 'var(--text-secondary)' }}>Ładowanie…</div>}
        {error && (
          <div role="alert" style={banner('error')}>
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}
        {copyMsg && (
          <div role={copyMsg.kind === 'error' ? 'alert' : 'status'} style={banner(copyMsg.kind)}>
            <Icon name={copyMsg.kind === 'error' ? 'triangle-alert' : 'check'} size={18} />
            <span>{copyMsg.text}</span>
          </div>
        )}
        {data && data.skipped.length > 0 && (
          <div style={{ color: 'var(--status-rec)', fontSize: 'var(--text-label)' }}>
            Pominięto {budynkiLabel(data.skipped.length)} (brak dostępu lub budynek nie istnieje).
          </div>
        )}
        {data && (
          <div style={{ fontWeight: 'var(--weight-semibold)' as any, color: 'var(--text-heading)' }}>
            {total === 0
              ? 'Wszystkie lokale sprawdzone.'
              : `Niesprawdzone: ${lokaleLabel(total)} · ${budynkiLabel(data.buildings.filter((b) => b.apartments.length > 0).length)}`}
          </div>
        )}
        {data && total > 0 && (
          <div style={{ overflow: 'auto', maxHeight: '60vh', display: 'grid', gap: 'var(--space-5)' }}>
            {data.buildings.filter((b) => b.apartments.length > 0).map((b) => {
              const g = groupUnchecked(b.apartments);
              return (
                <section key={b.buildingId} aria-label={b.address}>
                  <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body)', color: 'var(--text-heading)' }}>
                    {b.address}{' '}
                    <span style={{ fontWeight: 'var(--weight-regular)' as any, color: 'var(--text-secondary)' }}>
                      ({b.apartments.length} z {b.apartmentsTotal})
                    </span>
                  </h3>
                  {/* E13/X10: wyraźne grupy Nieobecność / Odmowa / Niesprawdzone (puste pomijane). */}
                  {UNCHECKED_GROUP_ORDER.filter((k) => g[k].length > 0).map((k) => (
                    <div key={k} role="group" aria-label={`${UNCHECKED_GROUP_LABELS[k]} — ${b.address}`} data-testid={`unchecked-group-${k}`}
                      style={{ marginTop: 'var(--space-3)' }}>
                      <h4 style={groupTitle}>
                        {UNCHECKED_GROUP_LABELS[k]} <span style={{ fontWeight: 'var(--weight-regular)' as any, color: 'var(--text-secondary)' }}>({g[k].length})</span>
                      </h4>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
                        <tbody>{aptRows(g[k])}</tbody>
                      </table>
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </ModalShell>
  );
}
